import {Observable} from 'rxjs';
import i18next from 'i18next';

import {combineEpics} from 'web-app/util/redux-observable';
import * as Api from 'web-app/react/inquiries/api';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import * as ChildActions from 'web-app/react/entities/children/actions';
import {track} from 'web-app/util/famlytics/index';
import {TrackingEvents} from 'web-app/util/famlytics/events';
import * as ContactActions from 'web-app/react/entities/contacts/actions';
import {showDeleteDialogObservable} from 'web-app/react/dialog';

import * as Actions from './actions';

const getChildAndRelationActions = (child, relations) => [
    ChildActions.fetchSuccess.action(child.childId, child),
    ContactActions.fetchChildContactsSuccess.action({childId: child.childId, relations}),
];

const createInquiryActionObservable = (inquiryId, inquiryAction) => {
    return Api.createInquiryAction(inquiryId, inquiryAction).map(inquiryAction =>
        Actions.createInquiryActionSuccess.action({inquiryAction}),
    );
};

const createInquiry = action$ =>
    action$.ofType(Actions.createInquiry.type).mergeMap(({payload}) =>
        Api.createInquiry(payload.institutionId, payload.inquiry)
            .flatMap(({inquiry, child, relations}) => {
                const successActions = [
                    ...getChildAndRelationActions(child, relations),
                    Actions.createInquirySuccess.action({inquiry}),
                ];

                // this path is only triggered if the showAround is checkin during creation
                if (payload.inquiryAction) {
                    return createInquiryActionObservable(inquiry.inquiryId, payload.inquiryAction)
                        .flatMap(action => successActions.concat([action]))
                        .catch(() =>
                            Observable.of(
                                ...successActions,
                                GlobalEventActions.updateError.action(
                                    i18next.t('inquiries.createSuccessButShowAroundFailed'),
                                    true,
                                ),
                            ),
                        );
                }

                return successActions;
            })
            .catch(e => Observable.of(Actions.createInquiryFailed.action(e))),
    );

const startIn = action$ =>
    action$.ofType(Actions.startIn.type).mergeMap(({payload}) =>
        Api.startIn(payload.inquiryId, payload.data)
            .flatMap(({inquiry}) => {
                track(TrackingEvents.INQUIRIES_ENROLLED, {
                    reason: inquiry.reason,
                    source: inquiry.source,
                    bookingSource: inquiry.bookingSource,
                });
                return [
                    Actions.startInSuccess.action({inquiry}),
                    GlobalEventActions.updateSuccess.action(
                        i18next.t('inquiries.willStartInRoom', {
                            name: payload.translationData.name,
                            room: payload.translationData.room,
                            date: payload.translationData.date,
                        }),
                    ),
                ];
            })
            .catch(e => Observable.of(Actions.startInFailed.action(e))),
    );

const fetchInquirySignupLink = action$ =>
    action$
        .ofType(Actions.fetchInquirySignupLink.type)
        .mergeMap(({payload}) =>
            Api.fetchInquirySignupLink(payload.institutionId).map(({link}) =>
                Actions.fetchInquirySignupLinkSuccess.action({link}),
            ),
        );

const updateInquiry = action$ =>
    action$.ofType(Actions.updateInquiry.type).mergeMap(({payload}) =>
        Api.updateInquiry(payload.updatedInquiry)
            .map(({inquiry}) => Actions.updateInquirySuccess.action({inquiry}))
            .catch(e => Observable.of(Actions.updateInquiryFailed.action(e))),
    );

const deleteInquiry = action$ =>
    action$.ofType(Actions.deleteInquiry.type).mergeMap(({payload}) => {
        return showDeleteDialogObservable({
            title: i18next.t('areYouSure'),
            description: i18next.t('inquiries.deleteInquiryDescription'),
        })
            .mergeMap(selection => {
                if (selection === 'OK') {
                    return Api.deleteInquiry(payload.inquiryId).flatMap(inquiryId => [
                        Actions.deleteInquirySuccess.action({inquiryId}),
                        GlobalEventActions.updateSuccess.action(i18next.t('inquiries.inquiryDeleted')),
                    ]);
                } else {
                    return Observable.of(Actions.deleteInquiryAborted.action());
                }
            })
            .catch(e => Observable.of(Actions.deleteInquiryFailed.action(e)));
    });
const createInquiryAction = action$ =>
    action$
        .ofType(Actions.createInquiryAction.type)
        .mergeMap(({payload}) =>
            createInquiryActionObservable(payload.inquiryId, payload.inquiryAction).catch(e =>
                Observable.of(Actions.createInquiryActionFailed.action(e)),
            ),
        );

export default combineEpics(
    startIn,
    createInquiry,
    updateInquiry,
    deleteInquiry,
    fetchInquirySignupLink,
    createInquiryAction,
);
